import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Users, Target, TrendingUp, CheckCircle, Sparkles, ArrowLeft, Quote, Zap, BarChart3, BookOpen, Shield } from 'lucide-react';

// Services Data
const servicesData = {
  'corporate-training': {
    title: 'Corporate Training',
    subtitle: 'Transform your workforce with programs that drive performance',
    icon: Users,
    description: `Our corporate training programs are meticulously designed to build critical capabilities 
      across your organization. We don't believe in one-size-fits-all solutions. Instead, we combine 
      proven methodologies with innovative approaches to create immersive learning experiences that 
      stick, drive measurable results, and create lasting behavioral change.`,
    longDescription: `In today's rapidly evolving business landscape, your people are your greatest 
      competitive advantage. But traditional training often fails to deliver lasting impact. 
      At Expertize Edge, we've reimagined corporate training to be engaging, practical, and 
      directly tied to business outcomes. Our facilitators bring real-world experience and a 
      passion for developing others, creating environments where participants don't just learn—they transform.`,
    audience: 'Teams, managers, and organizations seeking to upskill their workforce and build new capabilities.',
    benefits: [
      { title: 'Customized Learning', description: 'Every program is tailored to your industry, culture, and specific challenges.' },
      { title: 'Measurable Impact', description: 'We establish clear metrics and track ROI throughout the engagement.' },
      { title: 'Expert Facilitators', description: 'Our trainers bring decades of real-world leadership experience.' },
      { title: 'Lasting Change', description: 'We focus on behavior change, not just knowledge transfer.' },
    ],
    deliverables: [
      'Customized curriculum development aligned with your strategic objectives',
      'Interactive workshop facilitation with hands-on exercises',
      'Leadership development programs for emerging and senior leaders',
      'Team building and collaboration training to improve cross-functional work',
      'Change management workshops to navigate organizational transformation',
      'Post-training assessment, coaching, and reinforcement activities',
      'Train-the-trainer programs to build internal capability',
      'Virtual and hybrid learning solutions for distributed teams',
    ],
    process: [
      { step: 1, title: 'Discovery & Assessment', description: 'We conduct stakeholder interviews, analyze current capabilities, and identify performance gaps that training can address.' },
      { step: 2, title: 'Program Design', description: 'We create customized content, learning activities, and assessments aligned with your objectives and organizational culture.' },
      { step: 3, title: 'Delivery Excellence', description: 'Expert facilitators deliver engaging, interactive sessions using adult learning principles and real-world applications.' },
      { step: 4, title: 'Reinforcement & Evaluation', description: 'We measure impact, provide coaching support, and recommend next steps to sustain learning gains.' },
    ],
    testimonial: {
      quote: "Expertize Edge's training programs transformed how our team approaches problems. Our engagement scores increased by 35% and we've seen measurable improvements in cross-team collaboration.",
      author: 'Verified Client',
      role: 'Learning & Development Leader',
      company: '',
    },
    stats: [
      { value: '35%', label: 'Average engagement increase' },
      { value: '500+', label: 'Leaders trained' },
      { value: '92%', label: 'Would recommend' },
    ],
    color: 'from-brand-purple to-brand-purple-mid',
  },
  consulting: {
    title: 'Consulting & Strategy',
    subtitle: 'Navigate complexity with clarity and confidence',
    icon: Target,
    description: `Our strategic consulting services help organizations tackle their most pressing 
      challenges and capitalize on emerging opportunities. We bring analytical rigor, deep industry 
      expertise, and creative problem-solving to deliver actionable strategies that drive sustainable 
      growth and competitive advantage.`,
    longDescription: `The business environment has never been more complex. Disruption comes from 
      unexpected directions, competitive advantages erode faster than ever, and leaders must make 
      high-stakes decisions with incomplete information. That's where we come in. Our consultants 
      partner with you as trusted advisors—not just analysts who deliver reports and disappear. 
      We work alongside your team, transfer knowledge, and ensure our recommendations don't just 
      make sense on paper but actually get implemented.`,
    audience: 'Executives and leadership teams facing strategic decisions, operational challenges, or transformation initiatives.',
    benefits: [
      { title: 'Pragmatic Approach', description: 'We focus on actionable recommendations, not theoretical frameworks.' },
      { title: 'Implementation Focus', description: 'We stay engaged through execution to ensure results are achieved.' },
      { title: 'Knowledge Transfer', description: 'We build your team\'s capabilities while solving problems together.' },
      { title: 'Industry Expertise', description: 'Our consultants bring deep experience across sectors.' },
    ],
    deliverables: [
      'Strategic planning and multi-year roadmapping',
      'Operational efficiency assessments and improvement programs',
      'Market analysis, competitive intelligence, and growth strategy',
      'Organizational design, restructuring, and capability building',
      'Digital transformation advisory and technology strategy',
      'M&A due diligence and post-merger integration support',
      'Implementation support and change management',
      'Board and executive advisory services',
    ],
    process: [
      { step: 1, title: 'Deep Discovery', description: 'We immerse ourselves in your business context, interview key stakeholders, and analyze data to understand the full picture.' },
      { step: 2, title: 'Rigorous Analysis', description: 'We apply proven frameworks and analytical techniques to diagnose root causes and identify opportunities.' },
      { step: 3, title: 'Strategy Development', description: 'We develop actionable recommendations with clear priorities, business cases, and implementation roadmaps.' },
      { step: 4, title: 'Implementation Support', description: 'We partner with your team to execute, overcome obstacles, and ensure the strategy delivers results.' },
    ],
    testimonial: {
      quote: "The consulting engagement delivered measurable results within 90 days. Our operational efficiency improved by 40% and we identified new revenue streams we hadn't considered.",
      author: 'Verified Client',
      role: 'Senior Operations Executive',
      company: '',
    },
    stats: [
      { value: '40%', label: 'Avg. efficiency gain' },
      { value: '90', label: 'Days to first results' },
      { value: '3x', label: 'Typical ROI' },
    ],
    color: 'from-brand-gold to-brand-gold-lite',
  },
  coaching: {
    title: 'Coaching & Mentorship',
    subtitle: 'Unlock individual potential to strengthen organizational leadership',
    icon: TrendingUp,
    description: `Our personalized coaching programs accelerate leadership development by providing 
      dedicated support, challenge, and accountability. We help leaders discover their authentic 
      voice, navigate complex challenges, and develop the capabilities needed to excel at the 
      next level.`,
    longDescription: `Leadership can be lonely. The higher you rise, the fewer people you can 
      confide in, the more complex your decisions become, and the greater the consequences of 
      missteps. Executive coaching provides a confidential space to think out loud, gain perspective, 
      and develop as a leader with the support of someone who has been there. Our coaches are 
      experienced executives themselves—they've sat in the hot seat, made tough calls, and learned 
      from both successes and setbacks. They don't just ask questions; they share wisdom earned 
      through real experience.`,
    audience: 'Executives, emerging leaders, and high-potential individuals seeking personalized development.',
    benefits: [
      { title: 'Confidential Partnership', description: 'A trusted sounding board for your most challenging situations.' },
      { title: 'Experienced Coaches', description: 'Our coaches are former C-suite executives with real-world credibility.' },
      { title: 'Tailored Approach', description: 'Every coaching engagement is customized to your specific goals.' },
      { title: 'Measurable Growth', description: 'We track progress and adjust approach based on results.' },
    ],
    deliverables: [
      'One-on-one executive coaching with certified, experienced coaches',
      'Comprehensive leadership assessment and 360-degree feedback',
      'Personalized development planning with clear milestones',
      'Peer coaching circles for shared learning and accountability',
      'Mentorship program design and implementation for organizations',
      'Transition coaching for new roles, promotions, or career changes',
      'Team coaching for leadership teams and project groups',
      'Progress tracking, reporting, and stakeholder communication',
    ],
    process: [
      { step: 1, title: 'Assessment & Chemistry', description: 'We conduct assessments, gather 360 feedback, and ensure strong coach-client fit before beginning.' },
      { step: 2, title: 'Goal Setting & Planning', description: 'We collaboratively establish development goals and create a personalized coaching roadmap.' },
      { step: 3, title: 'Coaching Sessions', description: 'Regular sessions focused on real challenges, skill development, and accountability for commitments.' },
      { step: 4, title: 'Integration & Graduation', description: 'We help embed new behaviors, measure progress, and establish habits for continued growth.' },
    ],
    testimonial: {
      quote: "My coach helped me see blind spots I didn't know I had. I'm a more effective leader, a better communicator, and I've built a stronger team as a result.",
      author: 'Verified Client',
      role: 'CEO',
      company: '',
    },
    stats: [
      { value: '6mo', label: 'Typical engagement' },
      { value: '98%', label: 'Goal achievement' },
      { value: '85%', label: 'Report promotion' },
    ],
    color: 'from-brand-purple-lite to-brand-purple',
  },
};

// Services Index Page
const ServicesIndex = () => {
  const services = [
    { ...servicesData['corporate-training'], slug: 'corporate-training' },
    { ...servicesData['consulting'], slug: 'consulting' },
    { ...servicesData['coaching'], slug: 'coaching' },
  ];

  return (
    <main className="pt-20">
      {/* Hero */}
      <section className="relative py-32 bg-brand-charcoal overflow-hidden" data-testid="services-hero">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(92,45,145,0.3),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(196,158,77,0.2),transparent_50%)]" />
        </div>

        {/* Floating Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ y: [0, -15, 0], rotate: [0, 3, 0] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-1/3 right-1/5 w-48 h-48 bg-brand-purple/10 rounded-full blur-3xl"
          />
          <motion.div
            animate={{ y: [0, 15, 0], rotate: [0, -3, 0] }}
            transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
            className="absolute bottom-1/4 left-1/5 w-64 h-64 bg-brand-gold/10 rounded-full blur-3xl"
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 bg-brand-gold/20 border border-brand-gold/30 rounded-full px-4 py-2 mb-6"
              >
                <Zap className="w-4 h-4 text-brand-gold" />
                <span className="text-brand-gold text-sm font-medium">Our Services</span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6"
              >
                Comprehensive Solutions for{' '}
                <span className="relative">
                  <span className="text-brand-gold">Growth</span>
                  <motion.span
                    initial={{ width: 0 }}
                    animate={{ width: '100%' }}
                    transition={{ duration: 0.8, delay: 0.8 }}
                    className="absolute bottom-1 left-0 h-1 bg-brand-gold/40"
                  />
                </span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed mb-10"
              >
                From training to strategy to coaching, we offer integrated services 
                designed to elevate your organization's performance and leadership capabilities.
              </motion.p>

              {/* Quick stats row */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="flex justify-center gap-12"
              >
                {[
                  { value: '3', label: 'Core Services' },
                  { value: '500+', label: 'Leaders Served' },
                  { value: '95%', label: 'Satisfaction' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center">
                    <p className="text-3xl font-bold text-brand-gold">{stat.value}</p>
                    <p className="text-gray-400 text-sm">{stat.label}</p>
                  </div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Cards */}
      <section className="py-24 bg-brand-bg-light relative overflow-hidden" data-testid="services-cards">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-brand-lavender/50 to-transparent pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block bg-brand-purple/10 text-brand-purple text-sm font-semibold px-4 py-2 rounded-full mb-4">
              What We Offer
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-brand-charcoal mb-4">
              Three Pillars of <span className="text-brand-purple">Transformation</span>
            </h2>
            <p className="text-brand-muted text-lg max-w-2xl mx-auto">
              Each service is designed to create measurable, lasting impact for your organization.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
              >
                <Link
                  to={`/services/${service.slug}`}
                  className="group block bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-500 h-full border border-transparent hover:border-brand-purple/20 relative overflow-hidden"
                  data-testid={`service-card-${service.slug}`}
                >
                  {/* Hover gradient overlay */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} />

                  <div className="relative">
                    <div className={`w-16 h-16 bg-gradient-to-br ${service.color} rounded-2xl flex items-center justify-center mb-6 transform group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300`}>
                      <service.icon className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="font-heading text-2xl font-bold text-brand-charcoal mb-3 group-hover:text-brand-purple transition-colors">
                      {service.title}
                    </h2>
                    <p className="text-brand-muted mb-6 leading-relaxed">{service.subtitle}</p>

                    {/* Stats preview */}
                    <div className="flex flex-wrap gap-2 mb-6">
                      {service.stats.map((stat, i) => (
                        <span key={i} className="inline-block bg-brand-lavender px-3 py-1 rounded-full">
                          <span className="text-brand-purple text-sm font-semibold">{stat.value} {stat.label}</span>
                        </span>
                      ))}
                    </div>

                    <div className="flex items-center gap-2 text-brand-purple font-semibold">
                      <span>Explore Service</span>
                      <ArrowRight className="w-5 h-5 transform group-hover:translate-x-2 transition-transform duration-300" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Luminarc Feature */}
      <section id="luminarc" className="relative py-32 bg-brand-charcoal overflow-hidden" data-testid="services-luminarc">
        {/* Animated gradient background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(196,158,77,0.15),transparent_70%)]" />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 bg-brand-gold/20 border border-brand-gold/30 rounded-full px-4 py-2 mb-6">
                <Sparkles className="w-4 h-4 text-brand-gold" />
                <span className="text-brand-gold text-sm font-medium">Flagship Experience</span>
              </div>

              <h2 className="font-heading text-5xl sm:text-6xl font-bold text-white mb-6">
                Luminarc<span className="text-brand-gold">&trade;</span>
              </h2>

              <p className="text-2xl text-brand-gold-lite font-light italic mb-8 leading-relaxed">
                "Where logic ends, perspective begins."
              </p>

              <p className="text-gray-300 text-lg leading-relaxed mb-8">
                Luminarc&trade; is our signature hands-on experience designed to develop narrative 
                thinking in the age of AI. It's not a workshop—it's a transformational journey 
                for leaders navigating disruption and ambiguity.
              </p>

              <div className="space-y-4 mb-10">
                {[
                  'Structured thinking clarity in complex situations',
                  'Powerful storytelling that inspires action',
                  'Decision-making frameworks for uncertainty',
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    className="flex items-center gap-3"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="w-6 h-6 rounded-full bg-brand-gold/20 flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-brand-gold" />
                    </div>
                    <span className="text-gray-300">{item}</span>
                  </motion.div>
                ))}
              </div>

              <Link
                to="/luminarc"
                className="inline-flex items-center gap-3 bg-brand-gold text-brand-charcoal font-semibold px-8 py-4 hover:bg-brand-gold-lite hover:scale-105 transition-all duration-300 group"
                data-testid="luminarc-services-cta"
              >
                <span>Discover Luminarc&trade;</span>
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative hidden lg:block"
            >
              {/* Glowing orb effect */}
              <div className="absolute inset-0 flex items-center justify-center">
                <motion.div
                  animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.8, 0.5] }}
                  transition={{ duration: 4, repeat: Infinity }}
                  className="w-64 h-64 bg-brand-gold/20 rounded-full blur-3xl"
                />
              </div>

              <div className="relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 mx-8">
                <Quote className="w-10 h-10 text-brand-gold/30 mb-4" />
                <p className="text-white text-lg italic mb-6">
                  "Luminarc&trade; transformed how I approach every strategic decision. 
                  The narrative thinking framework gave our entire leadership team a new lens."
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-purple to-brand-gold flex items-center justify-center text-white font-bold">
                    ★
                  </div>
                  <div>
                    <p className="text-white font-semibold">Verified Client</p>
                    <p className="text-gray-400 text-sm">Executive Leader</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process Overview */}
      <section className="py-24 bg-white relative overflow-hidden" data-testid="services-process">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block bg-brand-gold/10 text-brand-gold text-sm font-semibold px-4 py-2 rounded-full mb-4">
              How We Work
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-brand-charcoal mb-4">
              Our <span className="text-brand-purple">Proven</span> Process
            </h2>
            <p className="text-brand-muted text-lg max-w-2xl mx-auto">
              A systematic approach that ensures measurable outcomes at every stage.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { icon: BookOpen, step: 'Discovery', description: 'We deeply understand your context, challenges, and objectives.' },
              { icon: Target, step: 'Design', description: 'We craft tailored solutions aligned with your specific needs.' },
              { icon: Zap, step: 'Deliver', description: 'We execute with excellence using proven methodologies.' },
              { icon: BarChart3, step: 'Evaluate', description: 'We measure impact and optimize for lasting results.' },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="relative group"
              >
                {/* Connector line */}
                {index < 3 && (
                  <div className="hidden md:block absolute top-10 left-[calc(50%+2rem)] w-[calc(100%-4rem)] h-0.5 bg-brand-lavender" />
                )}

                <div className="relative text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-brand-purple to-brand-purple-mid text-white rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300 shadow-lg">
                    <item.icon className="w-9 h-9" />
                  </div>
                  <div className="text-brand-purple font-bold text-sm mb-2">Step {index + 1}</div>
                  <h3 className="font-heading text-xl font-semibold text-brand-charcoal mb-2">
                    {item.step}
                  </h3>
                  <p className="text-brand-muted text-sm leading-relaxed">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-brand-charcoal to-brand-purple relative overflow-hidden" data-testid="services-cta">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }} />
        </div>

        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-white/80 text-xl mb-10 max-w-2xl mx-auto">
              Let's discuss which services best fit your organization's needs 
              and craft a path to your transformation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-charcoal font-semibold px-10 py-4 hover:bg-brand-gold-lite hover:scale-105 transition-all duration-300 group"
                data-testid="services-main-cta"
              >
                Book a Consultation
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/testimonials"
                className="inline-flex items-center justify-center gap-2 border-2 border-white/30 text-white font-semibold px-10 py-4 hover:bg-white/10 hover:border-white/50 transition-all duration-300"
              >
                See Client Results
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

// Service Detail Page
const ServiceDetail = () => {
  const { slug } = useParams();
  const service = servicesData[slug];

  if (!service) {
    return (
      <main className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-4xl font-bold text-brand-charcoal mb-4">
            Service Not Found
          </h1>
          <Link to="/services" className="text-brand-purple hover:underline">
            View All Services
          </Link>
        </div>
      </main>
    );
  }

  const Icon = service.icon;

  return (
    <main className="pt-20">
      {/* Hero */}
      <section className="relative py-32 bg-brand-charcoal overflow-hidden" data-testid="service-detail-hero">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(92,45,145,0.3),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_70%,rgba(196,158,77,0.2),transparent_50%)]" />
        </div>

        {/* Floating Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ y: [0, -12, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-1/3 right-1/4 w-40 h-40 bg-brand-purple/10 rounded-full blur-3xl"
          />
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            className="absolute bottom-1/4 left-1/4 w-56 h-56 bg-brand-gold/10 rounded-full blur-3xl"
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-brand-gold mb-8 hover:underline group"
            data-testid="back-to-services"
          >
            <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
            All Services
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center"
          >
            <div>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="inline-flex items-center gap-2 bg-brand-gold/20 border border-brand-gold/30 rounded-full px-4 py-2 mb-6"
              >
                <Icon className="w-4 h-4 text-brand-gold" />
                <span className="text-brand-gold text-sm font-medium">Service Detail</span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="font-heading text-5xl sm:text-6xl font-bold text-white mb-4 leading-tight"
              >
                {service.title}
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className="text-gray-300 text-xl max-w-xl leading-relaxed"
              >
                {service.subtitle}
              </motion.p>
            </div>

            {/* Stats Cards */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="hidden lg:grid grid-cols-3 gap-4"
            >
              {service.stats && service.stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6 text-center hover:bg-white/15 transition-all duration-300"
                >
                  <p className="text-3xl font-bold text-brand-gold">{stat.value}</p>
                  <p className="text-gray-300 text-xs mt-1">{stat.label}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-24 bg-brand-bg-light relative overflow-hidden" data-testid="service-overview">
        <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-r from-brand-lavender/50 to-transparent pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2"
            >
              <span className="inline-block bg-brand-purple/10 text-brand-purple text-sm font-semibold px-4 py-2 rounded-full mb-4">
                Overview
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-brand-charcoal mb-6">
                What We <span className="text-brand-purple">Deliver</span>
              </h2>
              <p className="text-brand-body text-lg leading-relaxed mb-6">
                {service.description}
              </p>
              {service.longDescription && (
                <p className="text-brand-body text-lg leading-relaxed mb-8">
                  {service.longDescription}
                </p>
              )}
              <div className="bg-white rounded-2xl p-6 border border-brand-lavender shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <Users className="w-5 h-5 text-brand-purple" />
                  <h3 className="font-semibold text-brand-charcoal">Who It's For</h3>
                </div>
                <p className="text-brand-muted">{service.audience}</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-white p-8 rounded-2xl shadow-lg sticky top-32 border border-brand-lavender">
                <h3 className="font-heading text-xl font-bold text-brand-charcoal mb-4">
                  Ready to Start?
                </h3>
                <p className="text-brand-muted mb-6">
                  Schedule a discovery call to discuss how we can help.
                </p>
                <Link
                  to="/contact"
                  className="block w-full text-center bg-brand-gold text-brand-charcoal font-semibold py-4 rounded-xl hover:bg-brand-gold-lite hover:scale-[1.02] transition-all duration-300"
                  data-testid="service-detail-cta"
                >
                  Get in Touch
                </Link>

                {/* Mobile stats */}
                <div className="mt-8 pt-6 border-t border-brand-lavender lg:hidden">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    {service.stats && service.stats.map((stat, index) => (
                      <div key={index}>
                        <p className="text-2xl font-bold text-brand-purple">{stat.value}</p>
                        <p className="text-xs text-brand-muted">{stat.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      {service.benefits && (
        <section className="py-24 bg-white" data-testid="service-benefits">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <span className="inline-block bg-brand-gold/10 text-brand-gold text-sm font-semibold px-4 py-2 rounded-full mb-4">
                Why Choose Us
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-brand-charcoal">
                The Expertize Edge <span className="text-brand-purple">Advantage</span>
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {service.benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group"
                >
                  <div className="bg-brand-bg-light rounded-2xl p-8 h-full border border-transparent hover:border-brand-purple/20 hover:shadow-xl transition-all duration-300 text-center">
                    <div className={`w-16 h-16 bg-gradient-to-br ${service.color || 'from-brand-purple to-brand-purple-mid'} rounded-2xl flex items-center justify-center mx-auto mb-4 transform group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300`}>
                      <CheckCircle className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-brand-charcoal mb-2">
                      {benefit.title}
                    </h3>
                    <p className="text-brand-muted text-sm leading-relaxed">{benefit.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Deliverables */}
      <section className="py-24 bg-brand-bg-light relative overflow-hidden" data-testid="service-deliverables">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-brand-lavender/30 to-transparent pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block bg-brand-purple/10 text-brand-purple text-sm font-semibold px-4 py-2 rounded-full mb-4">
              Deliverables
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-brand-charcoal mb-4">
              What You <span className="text-brand-purple">Get</span>
            </h2>
            <p className="text-brand-muted text-lg max-w-2xl mx-auto">
              Every engagement is tailored to your needs, but here's what you can typically expect.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {service.deliverables.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="group"
              >
                <div className="bg-white rounded-xl p-5 border border-brand-lavender hover:border-brand-purple/30 hover:shadow-lg transition-all duration-300 h-full">
                  <div className="w-8 h-8 bg-brand-purple/10 rounded-lg flex items-center justify-center mb-3 group-hover:bg-brand-purple/20 transition-colors">
                    <CheckCircle className="w-5 h-5 text-brand-purple" />
                  </div>
                  <span className="text-brand-body text-sm leading-relaxed">{item}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-white" data-testid="service-process">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block bg-brand-gold/10 text-brand-gold text-sm font-semibold px-4 py-2 rounded-full mb-4">
              Our Approach
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-brand-charcoal mb-4">
              Our <span className="text-brand-purple">Proven</span> Process
            </h2>
            <p className="text-brand-muted text-lg max-w-2xl mx-auto">
              A systematic approach that ensures measurable outcomes at every stage.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {service.process.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="relative group"
              >
                {/* Connector line */}
                {index < service.process.length - 1 && (
                  <div className="hidden md:block absolute top-10 left-[calc(50%+2rem)] w-[calc(100%-4rem)] h-0.5 bg-brand-lavender" />
                )}

                <div className="relative text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-brand-purple to-brand-purple-mid text-white rounded-2xl flex items-center justify-center mx-auto mb-4 text-2xl font-bold shadow-lg transform group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                    {item.step}
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-brand-charcoal mb-2">
                    {item.title}
                  </h3>
                  <p className="text-brand-muted text-sm leading-relaxed">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-24 bg-brand-bg-light" data-testid="service-value">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block bg-brand-purple/10 text-brand-purple text-sm font-semibold px-4 py-2 rounded-full mb-4">
                The Difference
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-brand-charcoal mb-6">
                Why This <span className="text-brand-purple">Matters</span> for Your Organization
              </h2>
              <div className="space-y-6">
                <div className="flex gap-4 group">
                  <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-red-200 transition-colors">
                    <Shield className="w-6 h-6 text-red-500" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-brand-charcoal mb-1">Without Expert Support</h4>
                    <p className="text-brand-muted text-sm">Trial and error approach, wasted resources, missed opportunities, and slower time to impact.</p>
                  </div>
                </div>
                <div className="flex gap-4 group">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-green-200 transition-colors">
                    <CheckCircle className="w-6 h-6 text-green-500" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-brand-charcoal mb-1">With Expertize Edge</h4>
                    <p className="text-brand-muted text-sm">Proven methodologies, accelerated results, knowledge transfer, and sustainable capability building.</p>
                  </div>
                </div>
              </div>
              <div className="mt-8 p-6 bg-white rounded-xl border border-brand-lavender shadow-sm">
                <p className="text-brand-purple font-semibold mb-4">Average Client Outcomes:</p>
                <div className="grid grid-cols-3 gap-4 text-center">
                  {service.stats && service.stats.map((stat, idx) => (
                    <div key={idx}>
                      <p className="text-2xl font-bold text-brand-charcoal">{stat.value}</p>
                      <p className="text-xs text-brand-muted">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-brand-charcoal rounded-2xl p-8 text-white relative overflow-hidden">
                {/* Background decoration */}
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(196,158,77,0.15),transparent_50%)]" />
                
                <div className="relative">
                  <h3 className="font-heading text-2xl font-bold mb-6">What Sets Us Apart</h3>
                  <div className="space-y-4">
                    {[
                      'Practitioners, not just theorists—we\'ve done what we teach',
                      'Customized to your context, not off-the-shelf solutions',
                      'Focus on outcomes, not just activities delivered',
                      'Knowledge transfer so you\'re not dependent on us',
                      'Ongoing support beyond the engagement',
                    ].map((point, idx) => (
                      <motion.div
                        key={idx}
                        className="flex items-start gap-3"
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: idx * 0.1 }}
                      >
                        <CheckCircle className="w-5 h-5 text-brand-gold flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300">{point}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-24 bg-brand-charcoal relative overflow-hidden" data-testid="service-testimonial">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(196,158,77,0.1),transparent_60%)]" />
        </div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }} />
        </div>

        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Quote className="w-16 h-16 text-brand-gold/30 mx-auto mb-8" />
            <p className="text-2xl sm:text-3xl text-white font-light italic mb-8 leading-relaxed">
              "{service.testimonial.quote}"
            </p>
            <div>
              <p className="text-brand-gold font-semibold text-lg">{service.testimonial.author}</p>
              <p className="text-gray-400">
                {service.testimonial.role}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-brand-charcoal to-brand-purple relative overflow-hidden" data-testid="service-detail-cta-banner">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }} />
        </div>

        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-6">
              Ready to Transform Your {service.title.split(' ')[0]}?
            </h2>
            <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto">
              Take the first step towards measurable, lasting impact for your organization.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-charcoal font-semibold px-10 py-4 hover:bg-brand-gold-lite hover:scale-105 transition-all duration-300 group"
                data-testid="service-detail-book-call"
              >
                Book a Discovery Call
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center justify-center gap-2 border-2 border-white/30 text-white font-semibold px-10 py-4 hover:bg-white/10 hover:border-white/50 transition-all duration-300"
              >
                View All Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export { ServicesIndex, ServiceDetail };
